import { EventEmitter, Inject, Injectable } from '@angular/core';
import { getBlob, getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { COMMONS_ENV, HttpService, NetworkService, ObservableService, ServiceEvent } from '@damofire/data-angular-commons';
import { logger, blobToBase64, MAX_QUERY_RESULTS, Query, RESET_VALUE, SearchResult } from '@damofire/data-commons';
import { AuthenticationService } from '@damofire/data-security';
import { EmbeddedAsset } from '../asset/asset.dto';
import { IssueCfgService } from '../config/issue-cfg.service';
import { Description, TypedDesQuery } from '../des.dto';
import { IMAGE_NOT_AVAILABLE, IMAGE_NOT_FOUND, Issue, IssueMedia, IssueUserType, LocalizedIssueComponent, LocalizedRiskLevel, Resolution } from './issue.dto';

@Injectable()
export abstract class IssueService extends ObservableService<Issue> {
  abstract getImage(media: IssueMedia, type: 'thumb' | 'web' | 'original'): Promise<string>;
  abstract deleteMedia(media: IssueMedia): Promise<void>;
  abstract uploadImage(issue: Issue, filename: string, bytes: string, mediaId?: string, annotations?: unknown[]): Promise<void>;
  abstract updateMedia(media: IssueMedia): Promise<void>;
  abstract create(divisionId: string, issue: Issue): Promise<Issue>;
  abstract search(divisionId: string, query: TypedDesQuery): Promise<SearchResult<Issue>>;
  protected abstract dbUpdate(divisionId: string, dbIssue: Issue, beforeIssue: Issue): Promise<void>;
  abstract delete(divisionId: string, issues: Issue[]): Promise<void>;
  abstract get(divisionId: string, id: string): Promise<Issue>;
  abstract buildCustomItemList(divisionId: string, urlSearchString: string): Promise<string[]>;

  static getEffectiveWeight(issue: Issue): number {
    if (issue?.resolution === Resolution.RESOLVED) {
      return 0;
    } else {
      return issue?.riskLevel?.weight || 0;
    }
  }

  protected static getAssetDescription(asset: EmbeddedAsset): string {
    const base = `${asset.clientCode || ''} S${asset.system} U${asset.upright} ${asset.section || ''}`.trim();
    return `${base} ${asset.aisle || ''}`.trim();
  }

  static getResettableDescription(desc: LocalizedRiskLevel | LocalizedIssueComponent | typeof RESET_VALUE): Description {
    if (!desc || desc === RESET_VALUE || desc._id === RESET_VALUE) {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { localized, color, ...cleanDesc } = desc;
    return cleanDesc;
  }

  protected constructor(
    protected readonly issueCfgService: IssueCfgService,
    protected readonly appAuthService: AuthenticationService,
    @Inject(COMMONS_ENV) protected readonly environment: any,
    protected readonly http: HttpService,
    protected readonly networkService: NetworkService,
    protected readonly storage: Storage,
    protected readonly eventEmitter?: EventEmitter<ServiceEvent<Issue>>,
  ) {
    super(eventEmitter);
  }

  async getResolutionFromStatus(currentStatus: Description<number>, divisionId: string, organizationId: string): Promise<Resolution> {
    const resolvedStatus = await this.issueCfgService.getIssueResolvedStatus(divisionId, organizationId);
    return currentStatus?._id === resolvedStatus._id ? Resolution.RESOLVED : Resolution.OPEN;
  }

  async update(divisionId: string, issueDetails: Partial<Issue>, beforeIssue: Issue): Promise<void> {
    const dbIssue = await this.get(divisionId, issueDetails._id);

    if (dbIssue.repair?._id) {
      throw new Error('Cannot update issue that has been repaired.');
    }

    // TODO Merge lodash?
    if (issueDetails.component) {
      dbIssue.component = issueDetails.component;
    }

    if (issueDetails.description) {
      dbIssue.description = issueDetails.description;
    }

    if (issueDetails.riskLevel) {
      dbIssue.riskLevel = IssueService.getResettableDescription(issueDetails.riskLevel);
    }

    if (issueDetails.status) {
      dbIssue.status = IssueService.getResettableDescription(issueDetails.status);
      if (beforeIssue.organization?._id) {
        dbIssue.resolution = await this.getResolutionFromStatus(issueDetails.status, beforeIssue.division._id, beforeIssue.organization._id);
      } else if (issueDetails.resolution) {
        dbIssue.resolution = issueDetails.resolution;
      }
    }

    if (issueDetails.action) {
      dbIssue.action = IssueService.getResettableDescription(issueDetails.action);
    }

    if (issueDetails.issue !== undefined) {
      dbIssue.issue = issueDetails.issue;
    }

    if (issueDetails.equipment !== undefined) {
      dbIssue.equipment = issueDetails.equipment;
    }

    if (issueDetails.depth !== undefined) {
      dbIssue.depth = issueDetails.depth;
    }

    if (issueDetails.inspection !== undefined) {
      dbIssue.inspection = issueDetails.inspection;
    }

    if (issueDetails.repair !== undefined) {
      dbIssue.repair = issueDetails.repair;
    }

    dbIssue.reviewDate = issueDetails.reviewDate || null;
    dbIssue.coordinates = issueDetails.coordinates || null;

    await this.dbUpdate(divisionId, dbIssue, beforeIssue);
  }

  async updateAll(divisionId: string, issues: Issue[], update: Issue): Promise<void> {
    for (const issue of issues) {
      await this.update(divisionId, { ...update, _id: issue._id }, issue);
    }
  }

  async markAsRepaired(divisionId: string, issues: Issue[], organizationId: string): Promise<void> {
    const status = await this.issueCfgService.getIssueResolvedStatus(divisionId, organizationId);
    const user = await this.appAuthService.currentUser();
    const update = { resolution: Resolution.RESOLVED, status, repair: { by: user._id, date: new Date() } };

    for (const issue of issues) {
      await this.update(divisionId, { ...update, _id: issue._id }, issue);
    }
  }

  async applyRepair(divisionId: string, issues: Issue[], repairId: string, organizationId: string): Promise<void> {
    const status = await this.issueCfgService.getIssueResolvedStatus(divisionId, organizationId);
    const user = await this.appAuthService.currentUser();

    for (const issue of issues) {
      const dbIssue = await this.get(divisionId, issue._id);
      dbIssue.status = status;
      dbIssue.resolution = Resolution.RESOLVED;
      dbIssue.repair = { ...dbIssue.repair, _id: repairId, by: user._id, date: new Date() };
      await this.dbUpdate(divisionId, dbIssue, issue);
    }
  }

  async resetStatus(divisionId: string, issues: Issue[], organizationId: string): Promise<void> {
    const status = await this.issueCfgService.getIssueReportedStatus(divisionId, organizationId);
    const user = await this.appAuthService.currentUser();
    const update = { resolution: Resolution.OPEN, status, repair: { by: user._id, date: new Date() } };

    for (const issue of issues) {
      await this.update(divisionId, { ...update, _id: issue._id }, issue);
    }
  }

  async shareIssues(emails: string[], message: string, lang: string, divisionId: string, issueIds: number[]): Promise<void> {
    return this.http.post('ww-issues/share', { data: { emails, message, lang, divisionId, issueDetails: { issueIds } } });
  }

  async getCsv(query: Query): Promise<string> {
    return this.http.getByQueryCsv(`ww-issues`, query, null);
  }

  protected async resolveMediaImageRemote(media: IssueMedia, type: 'thumb' | 'web' | 'original'): Promise<string> {
    let url = IMAGE_NOT_AVAILABLE;

    if (this.networkService.connected && media.web) {
      logger.debug('Getting image from online source', { path: media[type], category: 'issue' });
      const file = ref(this.storage, media[type]);

      try {
        url = await getDownloadURL(file);
      } catch (err) {
        logger.debug('Image does not exist, falling back to original size', { path: media[type], category: 'issue' });
        const fallback = ref(this.storage, media.original);
        try {
          url = await getDownloadURL(fallback);
        } catch (err2) {
          logger.warn('Image not found', { category: 'issue', error: err2 });
          url = IMAGE_NOT_FOUND;
        }
      }
    }

    return url;
  }

  async downloadMedia(media: IssueMedia): Promise<string> {
    let base64: string;
    if (media.original) {
      const file = ref(this.storage, media.original);
      base64 = await blobToBase64(await getBlob(file));
    }

    return base64;
  }

  async getOpenIssuesWithoutInspection(divisionId: string): Promise<Issue[]> {
    const searchResult = await this.search(divisionId, { size: MAX_QUERY_RESULTS, filters: { resolution: Resolution.OPEN } });
    return searchResult.result.filter((issue) => !issue.inspection?._id);
  }

  protected async getUserType(): Promise<IssueUserType> {
    const roles = await this.appAuthService.roles();
    if (roles.includes('GSR_WATSON_REP')) {
      return IssueUserType.REP;
    } else if (roles.includes('GSR_WATSON_ADMIN')) {
      return IssueUserType.ENG;
    } else {
      return IssueUserType.USER;
    }
  }
}
